import { Permission } from "../types/permission.types";

export interface MenuItem {
  path: string;
  iconClass: string;
  text: string;
  permissions: string[];
  permissionCheckType?: 'all' | 'any';
  subItems?: MenuItem[];
}

export const menuItems: MenuItem[] = [
  {
    path: "/dashboard",
    iconClass: "fa-solid fa-display",
    text: "Dashboard",
    permissions: ["/dashboard"],
    permissionCheckType: "any",
  },
  {
    path: "/users",
    iconClass: "fa-solid fa-user",
    text: "Benutzer:in",
    permissions: ["/users"],
  },
  {
    path: "/message-center",
    iconClass: "fa-solid fa-handshake-angle",
    text: "Message Center",
    permissions: ["/message-center"],
  },
  {
    path: "/role-management",
    iconClass: "fa-solid fa-user-shield",
    text: "Rollenverwaltung",
    permissions: ["/role-management"],
  },
  {
    path: "/welcome-emails",
    iconClass: "fa-solid fa-envelope-circle-check",
    text: "Willkommens-E-Mails",
    permissions: [],
  },
  {
    path: "",
    iconClass: "fa-solid fa-clipboard-list",
    text: "Impact Eck",
    permissions: [],
    permissionCheckType: "any",
    subItems: [
      {
        path: "/challenges",
        iconClass: "fa-solid fa-trophy",
        text: "Challenges",
        permissions: ["/challenges", "/dashboard/challenges"],
      },
      {
        path: "/guides",
        iconClass: "fa-solid fa-handshake-angle",
        text: "Guides",
        permissions: ["/guides", "/dashboard/guides"],
      },
    ],
  },
  {
    path: "",
    iconClass: "fa-solid fa-user-circle",
    text: "Community",
    permissions: [],
    permissionCheckType: "any",
    subItems: [
      {
        path: "/news",
        iconClass: "fa-solid fa-newspaper",
        text: "News und Blogs",
        permissions: ["/news", "/dashboard/news"],
      },
      {
        path: "/post",
        iconClass: "fa-solid fa-signs-post",
        text: "Schwarzes Brett",
        permissions: ["/post", "/dashboard/bulletin"],
      },
      {
        path: "/survey",
        iconClass: "fa-solid fa-square-poll-vertical",
        text: "Umfragen",
        permissions: ["/survey"],
      },
      {
        path: "/company",
        iconClass: "fa-solid fa-building",
        text: "Unternehmensprofile",
        permissions: ["/company", "/dashboard/company"],
      },
      {
        path: "/warning",
        iconClass: "fa-solid fa-triangle-exclamation",
        text: "Warnungen",
        permissions: ["/warning", "/dashboard/warning"],
      },
      {
        path: "/document",
        iconClass: "fa-solid fa-file",
        text: "Objekt Dokumente",
        permissions: ["/document"],
      },
    ],
  },
  {
    path: "",
    iconClass: "fa-solid fa-display",
    text: "Dein Keb",
    permissions: [],
    permissionCheckType: "any",
    subItems: [
      {
        path: "/service-category",
        iconClass: "fa fa-wrench",
        text: "Concierge Service",
        permissions: ["/service-category"],
      },
      {
        path: "/service-category-items",
        iconClass: "fa fa-tasks",
        text: "Unterkategorie",
        permissions: ["/service-category-items", "/dashboard/subcategory"],
      },
      {
        path: "/improvements",
        iconClass: "fa-solid fa-lightbulb",
        text: "Verbesserungen",
        permissions: ["/improvements"],
      },
    ],
  },
  {
    path: "",
    iconClass: "fa-solid fa-sliders",
    text: "Einstellungen",
    permissions: [],
    permissionCheckType: "any",
    subItems: [
      {
        path: "/legal-notice",
        iconClass: "fa-solid fa-clipboard-list",
        text: "Rechtliche Hinweise",
        permissions: ["/legal-notice"],
      },
      {
        path: "/terms-and-conditions",
        iconClass: "fa-solid fa-check-double",
        text: "AGB & Privatsphäre",
        permissions: ["/terms-and-conditions"],
      },
      {
        path: "/privacy",
        iconClass: "fa-solid fa-unlock",
        text: "Impressum",
        permissions: ["/privacy", "/dashboard/imprint"],
      },
      {
        path: "/rating",
        iconClass: "fa-solid fa-star",
        text: "App Bewerten",
        permissions: ["/rating"],
      },
      {
        path: "/about-app",
        iconClass: "fa-solid fa-circle-info",
        text: "Über die App",
        permissions: ["/about-app"],
      },
    ],
  },
  {
    path: "",
    iconClass: "fa-solid fa-note-sticky",
    text: "Andere",
    permissions: [],
    permissionCheckType: "any",
    subItems: [
      {
        path: "/contact",
        iconClass: "fa-solid fa-headset",
        text: "Kontaktdaten",
        permissions: ["/contact"],
      },
      {
        path: "/wreath",
        iconClass: "fa fa-sun-o",
        text: "Kranzler Card",
        permissions: ["/wreath"],
      },
      {
        path: "/redeem-coins",
        iconClass: "fa-solid fa-coins",
        text: "Münzen einlösen",
        permissions: ["/redeem-coins"],
      },
    ],
  },
];

const hasViewPermission = (
  requiredPath: string,
  permissions: Permission[]
): boolean => {
  return permissions.some(
    (perm) => perm.menuPath === requiredPath && perm.canView === true
  );
};

const hasAnyDashboardPermission = (permissions: Permission[]): boolean => {
  return permissions.some(
    (perm) =>
      (perm.menuPath === '/dashboard' || perm.menuPath.startsWith('/dashboard/')) &&
      perm.canView === true
  );
};

export const getMenuItemsForRoleAndPermissions = (
  role: string,
  permissions: Permission[] = []
): MenuItem[] => {
  const isSuperAdmin = role === 'superadmin';

  const filterItems = (items: MenuItem[]): MenuItem[] => {
    return items
      .filter((item) => {
        if (isSuperAdmin) return true;
        if (item.permissions.length === 0 && item.subItems && item.subItems.length > 0) return true;
        if (item.permissions.length > 0) {
          if (item.path === '/dashboard') return hasAnyDashboardPermission(permissions);
          if (item.permissionCheckType === 'all') {
            return item.permissions.every(path => hasViewPermission(path, permissions));
          }
          return item.permissions.some(path => hasViewPermission(path, permissions));
        }
        return false;
      })
      .map(item => {
        if (item.subItems && item.subItems.length > 0) {
          const filteredSubItems = filterItems(item.subItems);
          if (filteredSubItems.length > 0) {
            return { ...item, subItems: filteredSubItems };
          }
          return null;
        }
        return item;
      })
      .filter((item): item is MenuItem => item !== null);
  };

  return filterItems(menuItems);
};
